import { generateId } from "@fleet/utils";
import { createSignal, For, Show } from "solid-js";
import { Check, ListTodo, Plus, Trash2 } from "@fleet/icons";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { NotFoundState } from "@/components/ui/not-found-state";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { and, eq, project, type Project, task, type Task } from "@time-tracker/powersync";
import {
  cn,
  liveProjectFilters,
  liveTaskFilters,
  patchRowVerified,
  queryKeys,
} from "@time-tracker/shared";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { createEntityResolution } from "@/lib/use-entity-resolution";

interface ProjectTasksDialogProps {
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Project task management: create, complete/reopen, and delete.
 * Tasks can later be associated with time entries from the timer.
 */
export function ProjectTasksDialog(props: ProjectTasksDialogProps) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  const [newTaskName, setNewTaskName] = createSignal("");
  const [isAdding, setIsAdding] = createSignal(false);

  // Reactively verifies that the project still exists (and is still accessible):
  // if it is deleted/unsynced while the dialog is open, the prop id becomes
  // obsolete and we would show an empty, misleading task list.
  const projectQuery = createPowerSyncQuery<Project>({
    queryKey: () => queryKeys.chronos.projectDetail(props.project?.id ?? ""),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const projectId = props.project?.id;
      if (!dbInstance || !projectId) {
        return null;
      }
      return dbInstance
        .select()
        .from(project)
        .where(and(eq(project.id, projectId), ...liveProjectFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(props.project?.id) && props.open,
    suspense: false,
  });

  // Resolves the project: present / loading (sync) / not-found / offline.
  // Prevents showing tasks for a project that no longer exists or is inaccessible.
  const resolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => (projectQuery.data ?? []).length > 0,
    isPending: () => projectQuery.isLoading,
  });

  const tasksQuery = createPowerSyncQuery<Task>({
    queryKey: () => queryKeys.chronos.projectTasks(props.project?.id ?? ""),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const projectId = props.project?.id;
      if (!dbInstance || !projectId) {
        return null;
      }
      return dbInstance
        .select()
        .from(task)
        .where(and(eq(task.project_id, projectId), ...liveTaskFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(props.project?.id),
    suspense: false,
  });

  const pendingTasks = () => (tasksQuery.data ?? []).filter((row) => !row.completed);
  const completedTasks = () => (tasksQuery.data ?? []).filter((row) => Boolean(row.completed));

  const handleAddTask = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const projectId = props.project?.id;
    const name = newTaskName().trim();
    if (!dbInstance || !projectId || !name) {
      return;
    }
    setIsAdding(true);
    try {
      await dbInstance.insert(task).values({
        id: generateId(),
        project_id: projectId,
        name,
        completed: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setNewTaskName("");
    } catch (err) {
      console.error("Failed to add task", err);
      showToast(t(msg`Could not create the task`), "error");
    } finally {
      setIsAdding(false);
    }
  };

  const toggleTask = async (row: Task) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    const nowCompleted = !row.completed;
    try {
      await patchRowVerified(dbInstance, task, task.id, row.id, {
        completed: nowCompleted ? 1 : 0,
        completed_at: nowCompleted ? new Date().toISOString() : null,
      });
    } catch (err) {
      console.error("Failed to toggle task", err);
      showToast(t(msg`Could not update the task`), "error");
    }
  };

  const deleteTask = async (row: Task) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await patchRowVerified(dbInstance, task, task.id, row.id, {
        deleted_at: new Date().toISOString(),
      });
      showToast(t(msg`Task deleted`), "success");
    } catch (err) {
      console.error("Failed to delete task", err);
      showToast(t(msg`Could not delete the task`), "error");
    }
  };

  const taskRow = (row: Task) => (
    <div class="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5 group">
      <button
        type="button"
        onClick={() => void toggleTask(row)}
        aria-label={row.completed ? t(msg`Reopen task`) : t(msg`Complete task`)}
        class={cn(
          "w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors cursor-pointer",
          row.completed
            ? "bg-[#ceffdf]/20 border-[#ceffdf]/40 text-[#ceffdf]"
            : "border-white/20 hover:border-[#afc6ff]/60 text-transparent",
        )}
      >
        <Check class="h-3.5 w-3.5" />
      </button>
      <span
        class={cn(
          "flex-1 text-xs font-mono truncate",
          row.completed ? "text-muted-foreground line-through" : "text-foreground",
        )}
      >
        {row.name}
      </span>
      <button
        type="button"
        onClick={() => void deleteTask(row)}
        aria-label={t(msg`Delete task`)}
        class="opacity-0 group-hover:opacity-100 text-destructive/70 hover:text-destructive transition-all p-1 rounded-md hover:bg-destructive/10 cursor-pointer"
      >
        <Trash2 class="h-3.5 w-3.5" />
      </button>
    </div>
  );

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      contentClass="bg-[#121212] border border-white/10 rounded-[24px] p-0 overflow-hidden max-w-md"
    >
      <div class="p-6 text-white">
        <DialogHeader showClose border={false} class="p-0 mb-4 text-white">
          <DialogTitle class="text-xl font-bold font-mono flex items-center gap-2">
            <ListTodo class="h-5 w-5 text-[#afc6ff]" />
            {t(msg`Tasks`)} · {props.project?.name ?? ""}
          </DialogTitle>
          <DialogDescription class="text-xs text-white/60 font-mono mt-1">
            {t(msg`Break the project into tasks and attach them to your time entries.`)}
          </DialogDescription>
        </DialogHeader>

        <Show
          when={resolution() === "ready" || resolution() === "loading"}
          fallback={
            <NotFoundState
              variant={resolution() === "offline" ? "offline" : "not-found"}
              title={resolution() === "offline" ? undefined : t(msg`Project not found`)}
              description={
                resolution() === "offline"
                  ? undefined
                  : t(msg`This project may have been deleted, or you don't have access to it.`)
              }
              primaryAction={{
                label: t(msg`Close`),
                onClick: () => {
                  props.onOpenChange(false);
                },
              }}
              class="py-4"
            />
          }
        >
          <form onSubmit={handleAddTask} class="flex items-center gap-2 mb-4">
            <input
              type="text"
              placeholder={t(msg`New task...`)}
              value={newTaskName()}
              onInput={(e) => setNewTaskName(e.currentTarget.value)}
              class="flex-1 bg-black/40 border border-white/10 rounded-[16px] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-white placeholder-white/20 font-mono"
            />
            <Button
              type="submit"
              disabled={isAdding() || !newTaskName().trim() || !drizzle()}
              class="px-4 py-2.5 bg-primary text-black rounded-full font-bold font-mono text-xs cursor-pointer hover:bg-primary/95 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus class="h-4 w-4" />
            </Button>
          </form>

          <div class="space-y-2 max-h-72 overflow-y-auto pr-1">
            <Show
              when={(tasksQuery.data ?? []).length > 0}
              fallback={
                <p class="text-xs text-muted-foreground font-mono text-center py-6">
                  {t(msg`No tasks yet. Create the first one above.`)}
                </p>
              }
            >
              <For each={pendingTasks()}>{taskRow}</For>
              <Show when={completedTasks().length > 0}>
                <p class="text-[9px] font-bold text-white/40 uppercase tracking-widest font-mono pt-2">
                  {t(msg`Completed`)}
                </p>
                <For each={completedTasks()}>{taskRow}</For>
              </Show>
            </Show>
          </div>
        </Show>
      </div>
    </Dialog>
  );
}
